/**
 * Edit Structure Tool - Schema
 *
 * Schema for structure operations: sheets, rows, columns, ranges.
 */
import { z } from "zod";
// ═══════════════════════════════════════════════════════════════════════════
// OPERATION TYPES
// ═══════════════════════════════════════════════════════════════════════════
export const StructureOperationTypeSchema = z.enum([
    // Sheet operations
    "insert_sheet",
    "copy_sheet",
    "copy_sheet_structure",
    "delete_sheet",
    "rename_sheet",
    "move_sheet",
    "set_sheet_tab_color", // New: set sheet tab color
    // Row/column operations
    "insert_rows",
    "insert_columns",
    "delete_rows",
    "delete_columns",
    "group_rows",
    "ungroup_rows",
    "group_columns",
    "ungroup_columns",
    "set_outline_level",
    "set_gridlines",
    // Range operations
    "copy_range",
    "move_range",
    "clear_range",
]);
// ═══════════════════════════════════════════════════════════════════════════
// SHARED SCHEMAS
// ═══════════════════════════════════════════════════════════════════════════
/**
 * RGB color as [R, G, B] array (each 0-255)
 */
const RgbColorSchema = z.tuple([
    z.number().int().min(0).max(255),
    z.number().int().min(0).max(255),
    z.number().int().min(0).max(255),
]).describe('RGB color as [R, G, B], e.g., [31, 73, 125] for Excel blue');
// ═══════════════════════════════════════════════════════════════════════════
// OPERATION PARAMS
// ═══════════════════════════════════════════════════════════════════════════
const InsertSheetParamsSchema = z.object({
    name: z.string().describe('Name for the new sheet'),
    position: z.number().int().min(0).optional().describe('Index position (0-based)'),
    afterSheet: z.string().optional().describe('Insert after this sheet name'),
    tabColor: RgbColorSchema.optional().describe('Tab color for the new sheet'),
});
const CopySheetParamsSchema = z.object({
    sourceSheet: z.string().describe('Sheet to copy'),
    newName: z.string().describe('Name for the copy'),
    position: z.number().int().min(0).optional().describe('Index position'),
    tabColor: RgbColorSchema.optional().describe('Tab color for the copied sheet'),
});
const CopySheetStructureParamsSchema = z.object({
    sourceSheet: z.string().describe('Sheet to copy'),
    newName: z.string().describe('Name for the copy'),
    keep: z.array(z.enum(['formulas', 'formatting', 'hardcodes', 'text'])).optional().describe('What to keep (default: all)'),
});
const DeleteSheetParamsSchema = z.object({
    sheetName: z.string().describe('Sheet to delete'),
});
const RenameSheetParamsSchema = z.object({
    oldName: z.string().describe('Current sheet name'),
    newName: z.string().describe('New sheet name'),
    tabColor: RgbColorSchema.optional().describe('Optionally set tab color while renaming'),
});
const MoveSheetParamsSchema = z.object({
    sheetName: z.string().describe('Sheet to move'),
    position: z.number().int().min(0).optional().describe('Target index'),
    afterSheet: z.string().optional().describe('Move after this sheet'),
});
const SetSheetTabColorParamsSchema = z.object({
    sheetName: z.string().describe('Sheet to set tab color for'),
    color: RgbColorSchema.describe('Tab color as RGB array, e.g., [255, 0, 0] for red'),
});
/**
 * Insert rows - supports both full row and bounded insertion.
 *
 * For bounded insertion (within a range), provide targetRange.
 * This uses a safe insert algorithm that shifts content within bounds only.
 */
const InsertRowsParamsSchema = z.object({
    sheetName: z.string().describe('Target sheet'),
    position: z.number().int().min(1).describe('Row to insert at (1-indexed)'),
    direction: z.enum(['at', 'after']).optional().default('at').describe('Whether to insert at the position row or immediately after it'),
    count: z.number().int().min(1).optional().default(1).describe('Number of rows'),
    // Bounded insertion (optional - if omitted, inserts full rows)
    targetRange: z.string().optional().describe('Bounded range for insertion (e.g., "A1:D20") - only shifts within these columns'),
    // Format reference
    formatRefRow: z.number().int().min(1).optional().describe('Copy formatting from this row (1-indexed). Defaults to row above if not specified.'),
    copyFormatFromAbove: z.boolean().optional().default(true).describe('Fallback: copy formatting from row above (only used if formatRefRow not specified)'),
});
/**
 * Insert columns - supports both full column and bounded insertion.
 *
 * For bounded insertion (within a range), provide targetRange.
 * This uses a safe insert algorithm that shifts content within bounds only.
 */
const InsertColumnsParamsSchema = z.object({
    sheetName: z.string().describe('Target sheet'),
    position: z.number().int().min(1).describe('Column to insert at (1-indexed, A=1)'),
    direction: z.enum(['at', 'after']).optional().default('at').describe('Whether to insert at the position column or immediately after it'),
    count: z.number().int().min(1).optional().default(1).describe('Number of columns'),
    // Bounded insertion (optional - if omitted, inserts full columns)
    targetRange: z.string().optional().describe('Bounded range for insertion (e.g., "A1:D20") - only shifts within these rows'),
    // Format reference
    formatRefCol: z.number().int().min(1).optional().describe('Copy formatting from this column (1-indexed). Defaults to column left if not specified.'),
    copyFormatFromLeft: z.boolean().optional().default(true).describe('Fallback: copy formatting from column left (only used if formatRefCol not specified)'),
});
/**
 * Delete rows - supports both full row and bounded deletion.
 *
 * For bounded deletion (within a range), provide targetRange.
 * This shifts content within bounds up after deletion.
 */
const DeleteRowsParamsSchema = z.object({
    sheetName: z.string().describe('Target sheet'),
    startRow: z.number().int().min(1).describe('First row to delete (1-indexed)'),
    count: z.number().int().min(1).optional().default(1).describe('Number of rows'),
    // Bounded deletion (optional - if omitted, deletes full rows)
    targetRange: z.string().optional().describe('Bounded range for deletion (e.g., "A1:D20") - only shifts within these columns'),
});
/**
 * Delete columns - supports both full column and bounded deletion.
 *
 * For bounded deletion (within a range), provide targetRange.
 * This shifts content within bounds left after deletion.
 */
const DeleteColumnsParamsSchema = z.object({
    sheetName: z.string().describe('Target sheet'),
    startColumn: z.number().int().min(1).describe('First column to delete (1-indexed)'),
    count: z.number().int().min(1).optional().default(1).describe('Number of columns'),
    // Bounded deletion (optional - if omitted, deletes full columns)
    targetRange: z.string().optional().describe('Bounded range for deletion (e.g., "A1:D20") - only shifts within these rows'),
});
const GroupRowsParamsSchema = z.object({
    sheetName: z.string().describe('Target sheet'),
    startRow: z.number().int().min(1).describe('First row to group (1-indexed)'),
    endRow: z.number().int().min(1).describe('Last row to group (1-indexed, inclusive)'),
    isCollapsed: z.boolean().optional().default(false).describe('Whether to collapse grouped rows immediately'),
});
const UngroupRowsParamsSchema = z.object({
    sheetName: z.string().describe('Target sheet'),
    startRow: z.number().int().min(1).describe('First row to ungroup (1-indexed)'),
    endRow: z.number().int().min(1).describe('Last row to ungroup (1-indexed, inclusive)'),
    allLevels: z.boolean().optional().default(false).describe('Remove all group levels (runtime support varies)'),
});
const GroupColumnsParamsSchema = z.object({
    sheetName: z.string().describe('Target sheet'),
    startColumn: z.number().int().min(1).describe('First column to group (1-indexed, A=1)'),
    endColumn: z.number().int().min(1).describe('Last column to group (1-indexed, inclusive)'),
    isCollapsed: z.boolean().optional().default(false).describe('Whether to collapse grouped columns immediately'),
});
const UngroupColumnsParamsSchema = z.object({
    sheetName: z.string().describe('Target sheet'),
    startColumn: z.number().int().min(1).describe('First column to ungroup (1-indexed, A=1)'),
    endColumn: z.number().int().min(1).describe('Last column to ungroup (1-indexed, inclusive)'),
    allLevels: z.boolean().optional().default(false).describe('Remove all group levels (runtime support varies)'),
});
const SetOutlineLevelParamsSchema = z.object({
    sheetName: z.string().describe('Target sheet'),
    rowLevel: z.number().int().min(0).max(8).optional().default(0).describe('Row outline level to show (1-8). 0 leaves row axis unchanged.'),
    columnLevel: z.number().int().min(0).max(8).optional().default(0).describe('Column outline level to show (1-8). 0 leaves column axis unchanged.'),
});
const SetGridlinesParamsSchema = z.object({
    sheetName: z.string().describe('Target sheet'),
    visible: z.boolean().describe('Whether worksheet gridlines should be visible'),
});
/**
 * Copy range - supports single destination OR multiple destinations.
 *
 * Destinations can be specified as:
 * - Single cell (e.g., "Sheet2!E1") - copies to range starting at that cell
 * - Full range (e.g., "Sheet2!E1:H10") - must match source dimensions
 * - Multiple destinations (all must match source dimensions)
 */
const CopyRangeParamsSchema = z.object({
    source: z.string().describe('Source range (e.g., "Sheet1!A1:D10")'),
    // Support both single destination (backward compatible) and multiple destinations
    destination: z.string().optional().describe('Single destination (e.g., "Sheet2!E1" or "Sheet2!E1:H10")'),
    destinations: z.array(z.string()).optional().describe('Multiple destinations (e.g., ["Sheet2!E1:H10", "Sheet3!A1:D10"]) - all must match source dimensions'),
    pasteType: z.enum(['all', 'values', 'formulas', 'formats']).optional().default('all'),
}).refine((data) => data.destination !== undefined || data.destinations !== undefined, { message: "Either destination or destinations must be provided" });
/**
 * Move range - cuts and pastes to a new location.
 *
 * Destination can be:
 * - Single cell (e.g., "Sheet1!F1") - moves to range starting at that cell
 * - Full range (e.g., "Sheet1!F1:I10") - must match source dimensions
 */
const MoveRangeParamsSchema = z.object({
    source: z.string().describe('Source range (e.g., "Sheet1!A1:D10")'),
    destination: z.string().describe('Destination - cell or full range (e.g., "Sheet1!F1" or "Sheet1!F1:I10")'),
});
const ClearRangeParamsSchema = z.object({
    rangeAddress: z.string().describe('Range to clear'),
    clearType: z.enum(['all', 'values', 'formats', 'comments']).optional().default('all'),
});
// ═══════════════════════════════════════════════════════════════════════════
// MAIN SCHEMA
// ═══════════════════════════════════════════════════════════════════════════
export const StructureOperationSchema = z.object({
    operation: StructureOperationTypeSchema,
    params: z.record(z.string(), z.unknown()).describe('Operation-specific parameters'),
});
export const EditStructureParamsSchema = z.object({
    uiDescription: z.string()
        .describe("Brief description (max ~15 words) of what this tool call is attempting."),
    operations: z.array(StructureOperationSchema).min(1).describe('List of operations to execute'),
});
export { EditStructureParamsSchema as EditStructureToolParamsSchema };
